/**
 * Shared NET cell for the opener / unwinder / positions tables.
 *
 * Renders the signed `total YES − total NO` exposure (positive = excess YES)
 * off the live engine's `believedPosition`. Green when hedged, red when the
 * signed net exceeds the tolerance (`isUnhedged`). The tooltip breaks the net
 * out into Kalshi net / Poly YES / Poly NO.
 *
 * When no live `believedPosition` is present yet, it falls back to the slower
 * REST position group (per-side `contracts`) and renders the same signed net
 * in a neutral grey — or `—` when neither is available.
 */
import { Box, Tooltip } from '@mui/material';
import type { BelievedPosition } from '../types/entry.types';
import { IMBALANCE_TOLERANCE, isUnhedged, netBreakdown, signedNet } from './unhedged';

const HEDGED_COLOR = '#51cf66';
const UNHEDGED_COLOR = '#ff6b6b';
const REST_COLOR = 'grey.500';

/** A REST position group with the per-side contract counts NetCell needs. */
export interface RestNetSource {
  positions?: { platform: 'polymarket' | 'kalshi'; side: 'yes' | 'no'; contracts: number }[];
}

const signed = (n: number): string =>
  `${n > 0 ? '+' : ''}${Number.isInteger(n) ? n : n.toFixed(2)}`;

/**
 * Signed `YES − NO` from a REST position group. Polymarket YES + Kalshi YES
 * are positive; Polymarket NO + Kalshi NO are negative. Mirrors the
 * believed-position sign convention (excess-YES positive).
 */
export const restSignedNet = (rest: RestNetSource): number => {
  let net = 0;
  for (const p of rest.positions ?? []) {
    net += p.side === 'yes' ? p.contracts : -p.contracts;
  }
  return net;
};

interface NetCellProps {
  bp: BelievedPosition | null | undefined;
  rest?: RestNetSource;
}

export const NetCell = ({ bp, rest }: NetCellProps) => {
  if (bp) {
    const net = signedNet(bp);
    const unhedged = isUnhedged(bp);
    const { kalshiNet, polyYes, polyNo } = netBreakdown(bp);
    const kLabel =
      kalshiNet >= 0 ? `+${kalshiNet} NO` : `+${Math.abs(kalshiNet)} YES`;
    const title = `K: ${kLabel} · P: YES ${polyYes} / NO ${polyNo}`;
    return (
      <Tooltip title={title} placement="top">
        <Box
          component="span"
          data-net-state={unhedged ? 'unhedged' : 'hedged'}
          data-net-title={title}
          sx={{
            color: unhedged ? UNHEDGED_COLOR : HEDGED_COLOR,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {signed(net)}
        </Box>
      </Tooltip>
    );
  }

  if (rest && (rest.positions?.length ?? 0) > 0) {
    return (
      <Box
        component="span"
        data-net-state="rest"
        sx={{ color: REST_COLOR, fontVariantNumeric: 'tabular-nums' }}
      >
        {signed(restSignedNet(rest))}
      </Box>
    );
  }

  return (
    <Box component="span" data-net-state="empty" sx={{ color: REST_COLOR }}>
      —
    </Box>
  );
};

/**
 * REST "actual" NET cell — the signed `YES − NO` exposure read straight from
 * the held `prediction_positions`, independent of the live engine's belief.
 *
 * The live `NetCell` reads `believedPosition`, whose per-path counters only
 * tally contracts the engine filled THIS session — so a pre-existing position
 * (filled in a prior session / before a restart) reads net 0 there even when
 * the actual legs are imbalanced. This cell shows the real held imbalance, so
 * the operator can spot engine-belief-vs-reality drift at a glance. Coloured by
 * the same tolerance: green when hedged, red when the actual net is unhedged.
 */
export const RestNetCell = ({ rest }: { rest?: RestNetSource }) => {
  if (!rest || (rest.positions?.length ?? 0) === 0) {
    return (
      <Box component="span" data-actnet-state="empty" sx={{ color: REST_COLOR }}>
        —
      </Box>
    );
  }
  const net = restSignedNet(rest);
  const unhedged = Math.abs(net) > IMBALANCE_TOLERANCE;
  const title = `Actual held position (total YES − total NO, from positions): ${signed(net)}`;
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-actnet-state={unhedged ? 'unhedged' : 'hedged'}
        sx={{
          color: unhedged ? UNHEDGED_COLOR : HEDGED_COLOR,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {signed(net)}
      </Box>
    </Tooltip>
  );
};
